import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit';
import { monitoring } from 'common/services';

import { broadcastStarted_WS } from '../actions/broadcast.actions';
import { joinRoom_WS, natterEnded_WS } from '../actions/room.actions';
import {
  hostPresenceChanged,
  natteringRoomLeft,
  partnerPresenceChanged,
  setActiveEventId,
  setRematchCountdown,
} from '../eventSlice.actions';
import { selectNatteringRoundStartTime } from '../selectors';
import { type AloneRecoveryTracker, type AloneRound, createAloneRecoveryTracker } from './aloneRecoveryTracker';

import type { TypedStartListening } from '@reduxjs/toolkit';
import type { CombinedDispatch, RootState } from 'store';

type AppStartListening = TypedStartListening<RootState, CombinedDispatch>;

const newEpisodeId = (): string => {
  try {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  } catch {
    // fall through to the non-crypto fallback below
  }
  return `alone-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e9).toString(36)}`;
};

/**
 * Session-scoped singleton so an alone episode recorded in one room joins to a recovery that
 * happens after a rematch — which unmounts/remounts NatteringCall (key={roomId}). Keeping the
 * tracker here (not in the component) is the only place the episode survives the room switch.
 */
export const aloneRecoveryTracker: AloneRecoveryTracker = createAloneRecoveryTracker({
  track: (action, extra) => monitoring.trackAction(action, extra),
  now: () => Date.now(),
  newEpisodeId,
});

export const aloneTelemetryListener = createListenerMiddleware();
const startListening = aloneTelemetryListener.startListening as AppStartListening;

// A rematch arms on setRematchCountdown and is confirmed by the next joinRoom_WS. First-match joins
// also set the countdown, but there is no open alone episode then, so recordRematchConfirmed no-ops.
let rematchPending = false;

const roundOf = (state: RootState): AloneRound => ({
  id: state.event.natteringRoundId,
  startTime: selectNatteringRoundStartTime(state),
});

startListening({
  actionCreator: partnerPresenceChanged,
  effect: ({ payload: { present, context } }, { getState }) => {
    aloneRecoveryTracker.recordPresence(present, context, roundOf(getState() as RootState));
  },
});

startListening({
  actionCreator: hostPresenceChanged,
  effect: ({ payload: { present } }, { getState }) => {
    aloneRecoveryTracker.recordHostPresence(present, roundOf(getState() as RootState));
  },
});

startListening({
  actionCreator: setRematchCountdown,
  effect: ({ payload }) => {
    if (payload) rematchPending = true;
  },
});

startListening({
  actionCreator: joinRoom_WS,
  effect: () => {
    if (!rematchPending) return;
    aloneRecoveryTracker.recordRematchConfirmed();
    rematchPending = false;
  },
});

startListening({
  matcher: isAnyOf(broadcastStarted_WS, natterEnded_WS),
  effect: () => aloneRecoveryTracker.recordRoundEnded(),
});

startListening({
  actionCreator: natteringRoomLeft,
  effect: () => aloneRecoveryTracker.recordUserLeft(),
});

startListening({
  actionCreator: setActiveEventId,
  effect: () => {
    rematchPending = false;
    aloneRecoveryTracker.reset();
  },
});
